import { useState, type FormEvent } from 'react';
import type { EnvioCampana } from '../lib/supabase';
import { enviosCampanaService, type RespaldoTransferencia } from '../services/enviosCampanaService';
import type { OwnerFondoCampana } from '../types/fondosCampana';
import { useAuth } from '../context/AuthContext';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { entidadDelOwnerDeCampana } from '../utils/comprobanteEntidad';
import { puedeOperarDinero } from '../utils/permissions';
import { formatCurrency } from '../utils/formatters';
import { Button } from './ui/Button';
import { Checkbox } from './ui/Checkbox';
import { DialogShell } from './ui/DialogShell';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { Textarea } from './ui/Textarea';
import { ComprobanteDeTransferencia } from './comprobantes/ComprobanteDeTransferencia';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { accionesEnvioDisponibles, entidadDineroDelOwner, ETIQUETAS_ACCION, type AccionEnvioCampana } from './enviosCampana/accionesEnvio';
import { mensajeDeFalloAlSubir, resolverRespaldoTransferencia } from './enviosCampana/respaldoTransferencia';
import { construirOperacionEnvio, requiereDeclaracion, requiereMotivo } from './enviosCampana/operacionesEnvio';
import { useOperacionEnvio } from './enviosCampana/useOperacionEnvio';

interface Props {
  readonly envio: EnvioCampana;
  readonly owner: OwnerFondoCampana;
  readonly readOnly: boolean;
  readonly accion: AccionEnvioCampana;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

const DECLARACIONES: Readonly<Partial<Record<AccionEnvioCampana, string>>> = {
  confirmar: 'Confirmo que recibimos el importe completo.',
  cerrar: 'Declaro que esta transferencia no se realizó y el dinero no salió.',
};

export function EnvioCampanaOperacionModal({ envio, owner, readOnly, accion, onClose, onSuccess }: Props) {
  const { usuario } = useAuth();
  const operacion = useOperacionEnvio();
  const [actual,setActual] = useState(envio);
  const [motivo,setMotivo] = useState('');
  const [descripcion,setDescripcion] = useState(envio.descripcion ?? '');
  const [declarado,setDeclarado] = useState(false);
  const [errorValidacion,setErrorValidacion] = useState<string | null>(null);
  const [recargando,setRecargando] = useState(false);
  const [preparando,setPreparando] = useState(false);
  // Solo al corregir: reemplazar el comprobante es opcional y no se objeta (lo revisa quien recibe).
  const comprobante = useComprobanteFormulario({
    entidad: entidadDelOwnerDeCampana(owner),
    fechaMovimiento: null,
    permiteSubirDespues: false,
    pideMedioPago: true,
    sentidoDinero: 'egreso',
    obligatoriedad: 'nunca',
  });
  const bloqueado = preparando || operacion.procesando || operacion.incierto || operacion.completado;
  const autorizado = !readOnly && accionesEnvioDisponibles(actual, owner,
    puedeOperarDinero(usuario, entidadDineroDelOwner(owner))).includes(accion);

  const recargar = async () => {
    setRecargando(true);
    try {
      const registro = await enviosCampanaService.getById(actual.id);
      if (!registro) throw new Error('No encontrado');
      setActual(registro);
      operacion.revisionRecargada();
      setErrorValidacion('Revisa el estado y confirma nuevamente los hechos antes de continuar.');
      setDeclarado(false);
    } catch {
      setErrorValidacion('No pudimos recargar la rendición. Tus datos permanecen en el formulario.');
    } finally { setRecargando(false); }
  };

  const ejecutar = async (e: FormEvent) => {
    e.preventDefault();
    if (!autorizado) { setErrorValidacion('Esta operación corresponde a otra junta o el estado cambió.'); return; }
    if ((requiereMotivo(accion) && !motivo.trim()) || (requiereDeclaracion(accion) && !declarado)) {
      setErrorValidacion('Completa el motivo y confirma los hechos que corresponden.');
      return;
    }
    const errorComprobante = accion==='corregir' ? comprobante.validar() : null;
    if (errorComprobante) { setErrorValidacion(errorComprobante); return; }
    setErrorValidacion(null);
    // Se resuelve una vez: si el resultado es incierto, el reintento repite la misma operación con el mismo comprobante.
    let respaldo: RespaldoTransferencia | undefined;
    if (accion==='corregir' && comprobante.seleccion.tipo!=='ninguno') {
      setPreparando(true);
      try {
        respaldo = await resolverRespaldoTransferencia(comprobante);
      } catch (error_: unknown) {
        setErrorValidacion(mensajeDeFalloAlSubir(error_));
        return;
      } finally { setPreparando(false); }
    }
    const datos = { accion, envio:actual, motivo:motivo.trim(), descripcion:descripcion.trim(), declarado, respaldo };
    const resultado = await operacion.ejecutar(id => construirOperacionEnvio(datos,id));
    // La lectura posterior del panel tiene su propio estado de error.
    if (resultado.estado==='confirmado') { onClose(); onSuccess(); }
  };

  return <DialogShell isOpen onClose={onClose} title={ETIQUETAS_ACCION[accion]} size="md"
    loading={bloqueado || recargando} onBeforeClose={() => !bloqueado && !recargando}>
    <form onSubmit={ejecutar} className="space-y-4">
      <div className="rounded-xl bg-slate-50 p-4">
        <p className="font-semibold text-slate-800">Importe completo: {formatCurrency(actual.monto)}</p>
        <p className="text-sm text-slate-600">Revisión {actual.revision}. El importe, la procedencia y los destinos se conservan.</p>
      </div>
      <MensajeAccion accion={accion} />
      {accion!=='cerrar' && <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-500">Transferencia enviada</span>
        <ComprobanteDeTransferencia comprobante={actual.comprobante ?? null} />
      </div>}
      <FormErrorAlert error={errorValidacion ?? operacion.error} />
      {accion==='corregir' && <ComprobanteInput comprobante={comprobante} titulo="Reemplazar el comprobante de la transferencia"
        ayuda="Opcional: sin un archivo nuevo se conserva el comprobante actual."
        deshabilitado={bloqueado || recargando} />}
      {accion==='corregir' && comprobante.seleccion.tipo!=='ninguno' &&
        <MedioPagoFolio comprobante={comprobante} deshabilitado={bloqueado || recargando} />}
      <fieldset disabled={bloqueado || recargando} className="space-y-4">
        {accion==='corregir' && <Textarea id="corregir-envio-descripcion" label="Descripción corregida"
          value={descripcion} onChange={e => setDescripcion(e.target.value)} />}
        {requiereMotivo(accion) && <Textarea id="envio-operacion-motivo" label="Motivo" required
          value={motivo} onChange={e => setMotivo(e.target.value)} />}
        {requiereDeclaracion(accion) && <Checkbox label={DECLARACIONES[accion] ?? ''}
          checked={declarado} onChange={setDeclarado} />}
      </fieldset>
      {operacion.revisionObsoleta && <Button type="button" variant="secondary" isLoading={recargando}
        onClick={() => void recargar()}>Recargar rendición</Button>}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" disabled={bloqueado || recargando} onClick={onClose}>Cancelar</Button>
        <Button type="submit" isLoading={operacion.procesando || preparando}
          disabled={!autorizado || operacion.completado || operacion.revisionObsoleta || recargando || comprobante.subiendo}>
          {operacion.incierto ? 'Verificar operación' : ETIQUETAS_ACCION[accion]}
        </Button>
      </div>
    </form>
  </DialogShell>;
}

function MensajeAccion({ accion }: { readonly accion: AccionEnvioCampana }) {
  const mensajes: Readonly<Record<AccionEnvioCampana,string>> = {
    confirmar: 'Confirma el dinero efectivamente recibido. No realices otra transferencia.',
    observar: 'Observar pide corregir la rendición y mantiene inmovilizado el dinero enviado.',
    corregir: 'Corrige el documento y reenvía la misma rendición. No cambia el importe ni solicita otra transferencia.',
    cerrar: 'Esta declaración solo corresponde a una transferencia que nunca se realizó.',
  };
  return <p className="text-sm text-slate-600">{mensajes[accion]}</p>;
}
